import { AlertTriangleIcon, RefreshCwIcon } from 'lucide-react'
import type { ComponentType, ReactNode } from 'react'

import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { errorMessage } from '@/api/client'
import { cn } from '@/lib/utils'

/** Neutral, non-apologetic empty state. Always says what to do next. */
export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  className,
}: {
  icon?: ComponentType<{ className?: string }>
  title: string
  description: ReactNode
  action?: ReactNode
  className?: string
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-start gap-3 rounded-lg border border-dashed border-border px-6 py-10',
        className,
      )}
    >
      {Icon === undefined ? null : (
        <div className="rounded-md border border-border bg-[var(--surface)] p-2 text-muted-foreground">
          <Icon className="size-4" />
        </div>
      )}
      <div className="space-y-1.5">
        <p className="text-sm font-medium text-foreground">{title}</p>
        <div className="max-w-prose text-sm text-pretty text-muted-foreground">
          {description}
        </div>
      </div>
      {action}
    </div>
  )
}

export function ErrorState({
  error,
  onRetry,
  title = 'Could not load this',
  className,
}: {
  error: unknown
  onRetry?: () => void
  title?: string
  className?: string
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-start gap-3 rounded-lg border border-[color-mix(in_oklch,var(--destructive)_30%,var(--border))] bg-[color-mix(in_oklch,var(--destructive)_6%,var(--card))] px-6 py-6',
        className,
      )}
      role="alert"
    >
      <div className="flex items-start gap-3">
        <AlertTriangleIcon className="mt-0.5 size-4 shrink-0 text-destructive" />
        <div className="space-y-1">
          <p className="text-sm font-medium text-foreground">{title}</p>
          <p className="max-w-prose text-sm text-pretty text-muted-foreground">
            {errorMessage(error)}
          </p>
        </div>
      </div>
      {onRetry === undefined ? null : (
        <Button variant="outline" size="sm" onClick={onRetry} className="ml-7">
          <RefreshCwIcon className="size-3.5" />
          Try again
        </Button>
      )}
    </div>
  )
}

/** Row-shaped skeleton for table loading states. */
export function TableSkeleton({ rows = 3, columns = 4 }: { rows?: number; columns?: number }) {
  return (
    <div className="divide-y divide-border" aria-busy="true" aria-live="polite">
      {Array.from({ length: rows }, (_unused, rowIndex) => (
        <div key={rowIndex} className="flex items-center gap-4 px-4 py-3.5">
          {Array.from({ length: columns }, (_unusedCol, columnIndex) => (
            <Skeleton
              key={columnIndex}
              className={cn('h-4', columnIndex === 0 ? 'w-40' : 'w-24')}
            />
          ))}
        </div>
      ))}
      <span className="sr-only">Loading…</span>
    </div>
  )
}

/** Full-page centred spinner used while auth bootstraps. */
export function FullPageLoader({ label = 'Loading saltseed…' }: { label?: string }) {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-background">
      <div className="flex items-center gap-3 text-sm text-muted-foreground">
        <span className="size-3 animate-pulse rounded-full bg-primary" />
        {label}
      </div>
    </div>
  )
}
